/**
 * Granular selectors over `useUploadQueueStore`.
 *
 * List selectors return ID ARRAYS, not item arrays. Wrapping item-array
 * selectors with `useShallow` does not stop 60Hz tray re-renders:
 * `updateProgress` returns a fresh item object every axios tick — so the
 * arrays containing those active items always fail shallow-equal and the
 * badge/tray re-renders at frame rate anyway.
 *
 * ID arrays are stable across progress events: only MEMBERSHIP changes
 * (enqueue / status transition / dismiss) produce a new array ref.
 * `useShallow` wraps each list so a no-op `uploads` ref rewrite with
 * byte-identical IDs returns the same array reference.
 *
 * Row consumers (`UploadItemRow`) take an `id: string` prop and call
 * `useUploadById(id)` internally. List parents (the tray, the indicator)
 * iterate over the ID array and render one `<UploadItemRow id={id} />`
 * per ID, so per-row re-render granularity is preserved AND the parent
 * only re-renders when the SET of visible items changes — NOT on every
 * progress tick. This pattern is what makes the ID-array refactor pay
 * off in practice: returning IDs here buys nothing while a list parent
 * still subscribes to the full uploads map.
 *
 * `useUploadsBySource` returns IDs sorted ACTIVE-FIRST. The sort runs
 * inside the selector under `useShallow` so the array ref stays stable
 * when neither membership nor active/terminal partition changes. We
 * push the sort into the selector (rather than re-sorting in the tray)
 * so the tray never needs to read the items map.
 *
 * Booleans (`useHasActiveUploads`) and single-item lookups
 * (`useUploadById`) keep Zustand's default `Object.is` because their
 * output is already a primitive / stable item ref.
 *
 * Terminal items naturally disappear from these
 * arrays as the store removes them on the auto-dismiss timers; no extra
 * plumbing needed here.
 */
import { useShallow } from 'zustand/react/shallow';
import { useUploadQueueStore } from './upload-queue.store';
import type { UploadItem, UploadSource, UploadStatus } from './upload-queue.store.types';

function isActiveStatus(status: UploadStatus): boolean {
  return status === 'queued' || status === 'uploading';
}

export function useActiveUploads(): string[] {
  return useUploadQueueStore(
    useShallow((state) =>
      Object.values(state.uploads)
        .filter((u) => isActiveStatus(u.status))
        .map((u) => u.id),
    ),
  );
}

export interface UploadCounts {
  active: number;
  success: number;
  failed: number;
  /**
   * Cancelled items currently in the queue (auto-dismissed after 1s). Lets
   * the tray distinguish "queue empty" (`total === 0`) from "only cancels
   * remain, briefly" without recomputing from item arrays.
   */
  cancelled: number;
  total: number;
}

export function useUploadCounts(): UploadCounts {
  return useUploadQueueStore(
    useShallow((state) => {
      const items = Object.values(state.uploads);
      let active = 0;
      let success = 0;
      let failed = 0;
      let cancelled = 0;
      for (const item of items) {
        if (item.status === 'queued' || item.status === 'uploading') active += 1;
        else if (item.status === 'success') success += 1;
        else if (item.status === 'failed') failed += 1;
        else cancelled += 1; // exhaustive: only 'cancelled' remains
      }
      return { active, success, failed, cancelled, total: items.length };
    }),
  );
}

/**
 * IDs for `source`, sorted ACTIVE-FIRST (queued/uploading before
 * success/failed/cancelled). The tray groups by source and renders rows
 * in this order; pushing the sort into the selector keeps the tray free
 * of any subscription to the full uploads map.
 */
export function useUploadsBySource(source: UploadSource): string[] {
  return useUploadQueueStore(
    useShallow((state) => {
      const items = Object.values(state.uploads).filter((u) => u.source === source);
      // Stable sort: active items first, original insertion order preserved
      // within each partition. Array.prototype.sort on V8/Safari is stable.
      items.sort((a, b) => {
        const aRank = isActiveStatus(a.status) ? 0 : 1;
        const bRank = isActiveStatus(b.status) ? 0 : 1;
        return aRank - bRank;
      });
      return items.map((u) => u.id);
    }),
  );
}

export function useHasActiveUploads(): boolean {
  return useUploadQueueStore((state) =>
    Object.values(state.uploads).some((u) => isActiveStatus(u.status)),
  );
}

/**
 * Items that just reached `success` and have not yet been auto-removed.
 * Used by the sidebar badge for its transient green-check window.
 *
 * Restricted to `success` (not `cancelled`) because the badge only ever shows
 * green on success, never on cancel.
 */
export function useRecentlyCompleted(): string[] {
  return useUploadQueueStore(
    useShallow((state) =>
      Object.values(state.uploads)
        .filter((u) => u.status === 'success')
        .map((u) => u.id),
    ),
  );
}

export function useUploadById(id: string): UploadItem | undefined {
  return useUploadQueueStore((state) => state.uploads[id]);
}
